// Individual podcast episode landing page. Public, SEO-optimized:
//   - Real URL: /podcast/{slug}
//   - <title>, <meta description>, Open Graph + Twitter Card
//   - JSON-LD PodcastEpisode + PodcastSeries schema
//   - Native <audio> element so bots can find the enclosure URL
//   - Anchor deep-links + "Listen next" internal links for crawl depth
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/app/Logo";
import { ArrowLeft, Headphones, Clock, Calendar as CalendarIcon, ExternalLink, Users } from "lucide-react";

// Very small markdown renderer — good enough for show notes with paragraphs,
// bold, italic, links, bullet lists. Security notes:
//   1. All source text is HTML-escaped BEFORE any pattern replacements.
//   2. Link URLs are hard-restricted to https?:// via regex — no javascript:,
//      data:, or vbscript: URLs can slip through.
//   3. No raw HTML pass-through; even inline **/* patterns only wrap the
//      already-escaped text nodes.
//   4. Content authors are admins only (see /api/admin/podcast/episodes),
//      not end users, so the trust boundary is already low-risk. This is
//      belt-and-suspenders.
function renderMd(md) {
  if (!md) return "";
  const esc = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = esc(md).split(/\r?\n/);
  const out = [];
  let inList = false;
  const closeList = () => { if (inList) { out.push("</ul>"); inList = false; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line) { closeList(); continue; }
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push('<ul class="list-disc pl-6 my-3 space-y-1.5">'); inList = true; }
      out.push(`<li>${inlineMd(line.replace(/^\s*[-*]\s+/, ""))}</li>`);
      continue;
    }
    closeList();
    out.push(`<p class="my-3 leading-relaxed">${inlineMd(line)}</p>`);
  }
  closeList();
  return out.join("");
}
function inlineMd(text) {
  // Bold and italic wrap text nodes only — text is already HTML-escaped
  // upstream so the `<strong>`/`<em>` tags we introduce are the only HTML.
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    // Links: URL must start with http:// or https://. The `[^)\s]+` clamp
    // stops at whitespace or `)`, and the anchor label text is already
    // HTML-escaped in the caller.
    .replace(/\[(.+?)\]\((https?:\/\/[^)\s]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener nofollow" class="text-primary underline">$1</a>');
}

function formatPubDate(rfc822) {
  try {
    return new Date(rfc822).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return rfc822 || "";
  }
}

function pubDateIso(rfc822) {
  try { return new Date(rfc822).toISOString(); } catch { return ""; }
}

export default function PodcastEpisodePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const backTo = user?.id ? "/welcome" : "/";
  const canonical = `${window.location.origin}/podcast/${slug}`;

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: "", data: null });
    api.get(`/podcast/episodes/${encodeURIComponent(slug)}`)
      .then((res) => { if (!cancelled) setState({ loading: false, error: "", data: res.data }); })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || "Episode not found";
        setState({ loading: false, error: detail, data: null });
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (state.loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground" data-testid="episode-loading">Loading…</div>;
  }
  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-8 text-center" data-testid="episode-not-found">
        <Helmet><title>Episode not found · Poodle People Podcast</title><meta name="robots" content="noindex" /></Helmet>
        <p className="text-lg font-semibold">This episode couldn&apos;t be found.</p>
        <p className="text-sm text-muted-foreground max-w-md">{state.error}</p>
        <Button variant="outline" onClick={() => nav("/podcast")} data-testid="episode-back-to-hub">
          <ArrowLeft size={14} className="mr-1.5" /> Back to all episodes
        </Button>
      </div>
    );
  }

  const { show, episode, listen_next: listenNext } = state.data;
  const description = episode.meta_description || episode.subtitle || episode.description || show?.description || "";
  const ogImage = episode.og_image_url || episode.image || show?.image || "";
  const notesHtml = renderMd(episode.show_notes_md);

  // JSON-LD structured data. Two objects in one @graph so Google gets both
  // series and episode context in a single crawl.
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "PodcastSeries",
        "@id": `${window.location.origin}/podcast#series`,
        "name": show?.title || "Poodle People Podcast",
        "url": `${window.location.origin}/podcast`,
        "description": show?.description || "",
        "image": show?.image || "",
        "author": { "@type": "Organization", "name": show?.author || "Poodle People" },
      },
      {
        "@type": "PodcastEpisode",
        "@id": canonical,
        "url": canonical,
        "name": episode.title,
        "description": description,
        "datePublished": pubDateIso(episode.pub_date) || undefined,
        "image": ogImage || undefined,
        "partOfSeries": { "@id": `${window.location.origin}/podcast#series` },
        "episodeNumber": episode.episode_number || undefined,
        "associatedMedia": episode.audio_url ? {
          "@type": "MediaObject",
          "contentUrl": episode.audio_url,
          "encodingFormat": "audio/mpeg",
        } : undefined,
        "transcript": episode.transcript || undefined,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background" data-testid="episode-page">
      <Helmet>
        <title>{`${episode.title} · Poodle People Podcast`}</title>
        <meta name="description" content={description.slice(0, 160)} />
        <link rel="canonical" href={canonical} />
        {/* Open Graph — used by Facebook/LinkedIn/iMessage preview cards */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={episode.title} />
        <meta property="og:description" content={description.slice(0, 160)} />
        <meta property="og:url" content={canonical} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        {/* Twitter card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={episode.title} />
        <meta name="twitter:description" content={description.slice(0, 160)} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Slim public header */}
      <header className="border-b border-border bg-card/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to={backTo} data-testid="episode-page-home-link">
            <Logo size={56} textClassName="text-xl" />
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => nav("/podcast")}
            data-testid="episode-back-button"
          >
            <ArrowLeft size={16} className="mr-1.5" />
            All episodes
          </Button>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 sm:px-10 pt-8 sm:pt-12 pb-16">
        {/* Overline */}
        <div className="flex items-center gap-3 text-muted-foreground mb-4">
          <Headphones size={18} />
          <p className="text-xs uppercase tracking-[0.22em]" data-testid="episode-overline">
            {show?.title || "Poodle People Podcast"}
            {episode.episode_number ? ` · Episode ${episode.episode_number}` : ""}
          </p>
        </div>

        <h1 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tighter text-foreground" data-testid="episode-title">
          {episode.title}
        </h1>

        {/* Meta row: date + duration + guests */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground mt-4">
          {episode.pub_date && (
            <span className="inline-flex items-center gap-1.5" data-testid="episode-date">
              <CalendarIcon size={14} />
              {formatPubDate(episode.pub_date)}
            </span>
          )}
          {episode.duration && (
            <span className="inline-flex items-center gap-1.5" data-testid="episode-duration">
              <Clock size={14} />
              {episode.duration}
            </span>
          )}
          {episode.guest_names?.length ? (
            <span className="inline-flex items-center gap-1.5" data-testid="episode-guests">
              <Users size={14} />
              With {episode.guest_names.join(", ")}
            </span>
          ) : null}
        </div>

        {/* Cover + audio player */}
        <div className="mt-8 grid gap-6 sm:grid-cols-[220px_1fr]">
          {(episode.image || show?.image) && (
            <img
              src={episode.image || show?.image}
              alt={episode.title}
              className="w-[220px] h-[220px] rounded-lg object-cover border border-border"
              data-testid="episode-cover"
            />
          )}
          <div className="flex flex-col justify-center gap-4">
            {episode.audio_url && (
              /* Native audio so bots can see the enclosure URL and Google
                 shows a play button in results. */
              <audio
                controls
                preload="none"
                src={episode.audio_url}
                className="w-full"
                data-testid="episode-audio"
              >
                Your browser doesn&apos;t support the audio element.
              </audio>
            )}
            {episode.link && (
              <a
                href={episode.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                data-testid="episode-anchor-link"
              >
                Open on Anchor <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>

        {/* Show notes */}
        {notesHtml ? (
          <section className="mt-10" data-testid="episode-shownotes">
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground mb-1">Show Notes &amp; Transcript</h2>
            <div
              className="text-foreground/85 text-[15px]"
              // renderMd already escapes source markdown and only injects
              // trusted patterns. DOMPurify is defence-in-depth in case the
              // renderer is ever tweaked or replaced.
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(notesHtml, {
                  ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "u", "a", "ul", "ol", "li", "h1", "h2", "h3", "h4", "code"],
                  ALLOWED_ATTR: ["href", "target", "rel"],
                }),
              }}
            />
          </section>
        ) : episode.description ? (
          /* Fallback to RSS description when we don't have curated notes yet. */
          <section className="mt-10" data-testid="episode-rss-description">
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground mb-3">About this episode</h2>
            <p className="text-foreground/85 leading-relaxed">{episode.description}</p>
          </section>
        ) : null}

        {/* Transcript — always visible below show notes, indexed by Google. */}
        {episode.transcript ? (
          <section className="mt-10" data-testid="episode-transcript">
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground mb-3">Transcript</h2>
            <div className="rounded-lg border border-border bg-muted/20 p-5 sm:p-6">
              <div
                className="text-foreground/85 text-[14px] leading-relaxed whitespace-pre-line font-sans"
                data-testid="episode-transcript-body"
              >
                {episode.transcript}
              </div>
            </div>
          </section>
        ) : null}

        {/* Chapters */}
        {episode.chapters?.length ? (
          <section className="mt-10" data-testid="episode-chapters">
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground mb-3">Chapters</h2>
            <ul className="divide-y divide-border rounded-lg border border-border overflow-hidden">
              {episode.chapters.map((c) => (
                <li key={`${c.time}-${c.label}`} className="flex items-center gap-4 px-4 py-2.5 text-sm">
                  <span className="font-mono text-xs text-muted-foreground w-16 tabular-nums shrink-0">{c.time}</span>
                  <span className="text-foreground/85">{c.label}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Listen next */}
        {listenNext?.length ? (
          <section className="mt-14" data-testid="episode-listen-next">
            <h2 className="font-display text-2xl font-bold tracking-tight text-foreground mb-4">Listen next</h2>
            <div className="grid gap-3 sm:grid-cols-3">
              {listenNext.map((e) => (
                <Link key={e.slug} to={`/podcast/${e.slug}`} data-testid={`listen-next-${e.slug}`} className="group">
                  <Card className="border-border shadow-none rounded-lg hover:border-primary/40 transition-colors h-full">
                    <CardContent className="p-4">
                      {e.image && (
                        <img src={e.image} alt="" className="w-full aspect-square rounded object-cover mb-3" loading="lazy" />
                      )}
                      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">
                        {e.episode_number ? `Episode ${e.episode_number}` : "Episode"}
                      </p>
                      <p className="font-medium text-sm leading-snug group-hover:text-primary transition-colors">
                        {e.title}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        ) : null}
      </article>
    </div>
  );
}
